import { deburr } from 'es-toolkit/string';

import { locale } from '../i18n/i18n';

/**
 * Checks if a string has special characters.
 *
 * @param str String to check
 * @returns {boolean} Whether the string has special characters
 */
export function hasNonAsciiCharacters(str?: string): boolean {
  return str ? deburr(str) !== str : false;
}

/**
 * Returns "apples, oranges and bananas" given ["apples", "oranges", "bananas"].
 *
 * @param {string[]} list Array of strings
 * @returns {string} Joined list
 */
export function joinedList(list: string[]): string {
  return new Intl.ListFormat(locale, {
    style: 'long',
    type: 'conjunction',
  }).format(list);
}

export function levenshtein(a: string, b: string): number {
  const matrix: number[][] = [];

  const lenA = a.length;
  const lenB = b.length;

  for (let i = 0; i <= lenA; i++) {
    matrix[i] = [i];
  }

  const firstRow = matrix[0];

  if (!firstRow) {
    throw new Error('Matrix initialization failed');
  }

  for (let j = 0; j <= lenB; j++) {
    firstRow[j] = j;
  }

  for (let i = 1; i <= lenA; i++) {
    for (let j = 1; j <= lenB; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      const currentRow = matrix[i];
      const previousRow = matrix[i - 1];

      if (!currentRow || !previousRow) {
        throw new Error('Matrix row initialization failed');
      }

      const previousRowValue = previousRow[j];
      const currentRowPreviousValue = currentRow[j - 1];
      const previousRowPreviousValue = previousRow[j - 1];

      if (
        previousRowValue === undefined ||
        currentRowPreviousValue === undefined ||
        previousRowPreviousValue === undefined
      ) {
        throw new Error('Matrix value access failed');
      }

      currentRow[j] = Math.min(
        previousRowValue + 1,
        currentRowPreviousValue + 1,
        previousRowPreviousValue + cost,
      );
    }
  }

  const lastRow = matrix[lenA];

  if (!lastRow) {
    throw new Error('Matrix last row access failed');
  }

  const lastValue = lastRow[lenB];

  if (lastValue === undefined) {
    throw new Error('Matrix last value access failed');
  }

  return lastValue;
}

export function normalize(name: string): string {
  return deburr(name)
    .toLowerCase()
    .replace(/[^a-z ]/g, '')
    .trim();
}
